import { LoadingOverlay } from "@/components/LoadingOverlay";
import { Switch } from "@/components/ui/switch";
import { showApiError } from "@/lib/toast";
import { HospitalityMenuCategoryItem } from "@/lib/types";
import {
  addHospitalityMenuCategoryItemAllergy,
  deleteHospitalityMenuCategoryItemAllergy,
  getAllergy,
  getHospitalityMenuCategoryItemAllergy,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HospitalIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Props {
  categoryItem: HospitalityMenuCategoryItem;
}

const QUERY_KEY = "getHospitalityMenuCategoryItemAllergy";

export const UpdateHospitalityMenuCategoryItemAllergies: React.FC<Props> = ({ categoryItem }) => {
  const { t } = useTranslation();
  const { data: allergies, isLoading } = useQuery({
    queryKey: ["getAllergy"],
    queryFn: () =>
      getAllergy({
        numberOfObjectsPerPage: 1000,
        pageNumber: 1,
      }),
    refetchOnMount: true,
  });

  const { data: categoryItemAllergies, isLoading: isCategoryItemAllergiesLoading } = useQuery({
    queryKey: [QUERY_KEY, categoryItem.id],
    queryFn: () => getHospitalityMenuCategoryItemAllergy(categoryItem.id),
  });

  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (allergyId: number) => addHospitalityMenuCategoryItemAllergy(categoryItem.id, allergyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, categoryItem.id],
      });
    },
    onError: showApiError,
  });

  const deleteMutation = useMutation({
    mutationFn: (allergyId: number) => deleteHospitalityMenuCategoryItemAllergy(allergyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, categoryItem.id],
      });
    },
    onError: showApiError,
  });

  const allergiesData = allergies?.data?.result || [];
  const categoryItemAllergiesData = categoryItemAllergies?.data?.result || [];

  return (
    <LoadingOverlay
      isLoading={isLoading || isCategoryItemAllergiesLoading || addMutation.isPending || deleteMutation.isPending}
    >
      <div className="font-medium underline mb-2">{t("hospitalityMenu.allergyList")}</div>
      {allergiesData.map((allergy) => {
        const allergyRelation = categoryItemAllergiesData.find((item) => item.allergyId === allergy.id);
        const isAllergySelected = !!allergyRelation;

        return (
          <div key={allergy.id} className="flex items-center justify-between py-2 px-1 border-b hover:bg-muted">
            <span className="flex items-center gap-2">
              <HospitalIcon className="w-4 h-4 bg-muted" />
              {allergy.name}
            </span>
            <Switch
              checked={isAllergySelected}
              onCheckedChange={() => {
                if (isAllergySelected) {
                  deleteMutation.mutate(allergyRelation.id);
                } else {
                  addMutation.mutate(allergy.id);
                }
              }}
            />
          </div>
        );
      })}
    </LoadingOverlay>
  );
};
